import React, { useState, useRef } from "react";
import { ContentGrid, Grid2, Grid4, CoverImage, CoverPlaceholder, AvatarGrid, AvatarPresetButton, FileInput, FileInputLabel } from "../styles";
import { Card, Button, Field, Pill } from "../components";
import Icon from "../components/Icon";

const AVATAR_IMAGES_PATH = `${process.env.PUBLIC_URL || ""}/img/avatars`;

const AVATAR_PRESETS = [
  { id: "1", label: "Avatar 1", src: "1.jpg" },
  { id: "2", label: "Avatar 2", src: "2.jpg" },
  { id: "3", label: "Avatar 3", src: "3.jpg" },
  { id: "5", label: "Avatar 5", src: "5.jpg" },
];

const truncateAddress = (addr, start = 14, end = 4) => {
  if (!addr || typeof addr !== "string") return "";
  const s = addr.trim();
  if (s.length <= start + end) return s;
  return s.slice(0, start) + "..." + s.slice(-end);
};

const ProfileView = ({
  fullName,
  setFullName,
  initials,
  account,
  setAccount,
  accountLocked,
  setAccountLocked,
  accountCheck,
  setAccountCheck,
  accountLastChecked,
  setAccountLastChecked,
  email,
  setEmail,
  phone,
  setPhone,
  avatar,
  setAvatar,
  cover,
  setCover,
  wallets,
  setWallets,
  userData,
  pushToast,
  uid,
  setAudit,
  normalizeAccount,
  checkAccountUnique,
  uploadProfileImage,
  avatarUploading,
  pendingAvatarFile,
  onAvatarFileSelected,
  onPresetAvatarSelected,
  saveProfileImage,
  clearPendingAvatar,
  coverUploading,
  pendingCoverFile,
  onCoverFileSelected,
  uploadCoverImage,
  saveCoverImage,
  clearPendingCover,
  saveProfile,
}) => {
  const [profileEditing, setProfileEditing] = useState(false);
  const coverFileInputRef = useRef(null);
  const copyToClipboard = (text, label) => {
    if (text) {
      navigator.clipboard?.writeText(text);
      pushToast("Wallet", `${label} address copied to clipboard`);
    }
  };
  const [hoverAvatar, setHoverAvatar] = useState(false);

  const setAvatarFromFile = async (file) => {
    if (!file) return;
    if (onAvatarFileSelected) {
      onAvatarFileSelected(file);
    } else if (uploadProfileImage) {
      await uploadProfileImage(file);
    } else {
      const url = URL.createObjectURL(file);
      setAvatar(url);
      pushToast("Profile", "Profile avatar updated");
      setAudit((s) => [{ id: uid(), ts: "2026-01-08 09:10", actor: "user", type: "Profile", detail: "Avatar updated", severity: "low" }, ...s]);
    }
  };

  const setCoverFromFile = (file) => {
    if (!file) return;
    if (onCoverFileSelected) onCoverFileSelected(file);
    else {
      setCover(URL.createObjectURL(file));
      pushToast("Profile", "Cover image selected");
      setAudit((s) => [{ id: uid(), ts: "2026-01-08 09:11", actor: "user", type: "Profile", detail: "Cover image selected", severity: "low" }, ...s]);
    }
    if (uploadCoverImage) void uploadCoverImage(file);
  };

  return (
    <ContentGrid>
      <Card
        title="Profile"
        icon="user"
        right={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Pill tone={accountLocked ? "d" : accountCheck === "taken" ? "b" : accountCheck === "available" ? "a" : "c"}>
              {accountLocked
                ? "Account locked"
                : accountCheck === "checking"
                ? "Checking…"
                : accountCheck === "taken"
                ? "Not available"
                : accountCheck === "available"
                ? "Available"
                : "Identity"}
            </Pill>
          </div>
        }
      >
        <ContentGrid>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 72, height: 72, borderRadius: 18, overflow: "hidden", border: "1px solid rgba(92,233,255,.18)", background: "rgba(255,255,255,.06)", display: "grid", placeItems: "center" }}>
              {avatar ? <img src={avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Icon name="user" />}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span>{fullName}</span>
                <Pill tone="d">{account}</Pill>
              </div>
              <div style={{ fontSize: 12, color: "rgba(210,235,245,.78)" }}>{email}</div>
            </div>
          </div>

          <Grid4>
            <Field
              label="Account (@nickname)"
              value={account}
              disabled={!profileEditing || accountLocked}
              onChange={(v) => {
                const next = normalizeAccount(v);
                setAccount(next);
                if (next !== accountLastChecked) setAccountCheck("idle");
              }}
              placeholder="@nickname"
              right={
                <div style={{ width: 34, height: 34, borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(255,255,255,.06)", display: "grid", placeItems: "center" }} title={accountLocked ? "Locked" : "Handle"}>
                  <Icon name={accountLocked ? "lock" : "user"} />
                </div>
              }
            />
            <Field label="Full name" value={fullName} onChange={setFullName} disabled={!profileEditing} />
            <Field label="Email" value={email} onChange={setEmail} disabled={!profileEditing} />
            <Field label="Phone" value={phone} onChange={setPhone} disabled={!profileEditing} />
          </Grid4>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {profileEditing && (
                <>
                  <Button
                    variant="sec"
                    disabled={accountLocked}
                    onClick={() => {
                      void checkAccountUnique(account);
                      pushToast("Account", "Uniqueness check started");
                    }}
                  >
                    Check
                  </Button>
                  <Button
                    variant={accountLocked ? "ghost" : "sec"}
                    disabled={accountLocked || account.length < 3 || (accountCheck !== "available" && accountLastChecked === normalizeAccount(account))}
                    onClick={() => {
                      if (accountCheck !== "available") return pushToast("Account", "Please check availability first");
                      setAccountLocked(true);
                      pushToast("Account", "Account confirmed & locked");
                      setAudit((s) => [{ id: uid(), ts: "2026-01-08 09:12", actor: "user", type: "Profile", detail: "Account handle locked: " + account, severity: "med" }, ...s]);
                    }}
                  >
                    Confirm & lock
                  </Button>
                </>
              )}
              {accountLocked ? <Pill tone="d">Locked</Pill> : profileEditing ? <Pill tone="c">Editing</Pill> : null}
            </div>

            {profileEditing ? (
              <>
                <Button variant="ghost" onClick={() => setProfileEditing(false)}>
                  Cancel
                </Button>
                <Button
                  variant="sec"
                  icon="check"
                  disabled={avatarUploading}
                  onClick={async () => {
                    if (saveProfile) {
                      const ok = await saveProfile({ fullName, account, email, phone });
                      if (ok) setProfileEditing(false);
                    } else setProfileEditing(false);
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              <Button variant="sec" onClick={() => setProfileEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        </ContentGrid>
      </Card>

      <Card title="Wallet Addresses" icon="key" right={<Pill tone="d">Blockchain Wallets</Pill>}>
        <ContentGrid>
          <Grid2>
            {wallets?.sol && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.8)", fontWeight: 800 }}>Solana (SOL)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, padding: "8px 12px", borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(0,0,0,.22)", color: "rgba(240,250,255,.92)", fontSize: 12, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }} title={wallets.sol}>
                    {truncateAddress(wallets.sol)}
                  </div>
                  <Button variant="sec" onClick={() => copyToClipboard(wallets.sol, "Solana")} icon="copy" title="Copy address">
                    Copy
                  </Button>
                </div>
              </div>
            )}
            {wallets?.btc && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.8)", fontWeight: 800 }}>Bitcoin (BTC)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, padding: "8px 12px", borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(0,0,0,.22)", color: "rgba(240,250,255,.92)", fontSize: 12, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }} title={wallets.btc}>
                    {truncateAddress(wallets.btc)}
                  </div>
                  <Button variant="sec" onClick={() => copyToClipboard(wallets.btc, "Bitcoin")} icon="copy" title="Copy address">
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </Grid2>
          <Grid2>
            {wallets?.trc && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.8)", fontWeight: 800 }}>Tron (TRC20)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, padding: "8px 12px", borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(0,0,0,.22)", color: "rgba(240,250,255,.92)", fontSize: 12, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }} title={wallets.trc}>
                    {truncateAddress(wallets.trc)}
                  </div>
                  <Button variant="sec" onClick={() => copyToClipboard(wallets.trc, "Tron")} icon="copy" title="Copy address">
                    Copy
                  </Button>
                </div>
              </div>
            )}
            {wallets?.eth && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.8)", fontWeight: 800 }}>Ethereum (ETH)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, padding: "8px 12px", borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(0,0,0,.22)", color: "rgba(240,250,255,.92)", fontSize: 12, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }} title={wallets.eth}>
                    {truncateAddress(wallets.eth)}
                  </div>
                  <Button variant="sec" onClick={() => copyToClipboard(wallets.eth, "Ethereum")} icon="copy" title="Copy address">
                    Copy
                  </Button>
                </div>
              </div>
            )}
            {wallets?.bnb && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.8)", fontWeight: 800 }}>BSC (BNB)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, padding: "8px 12px", borderRadius: 12, border: "1px solid rgba(92,233,255,.14)", background: "rgba(0,0,0,.22)", color: "rgba(240,250,255,.92)", fontSize: 12, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }} title={wallets.bnb}>
                    {truncateAddress(wallets.bnb)}
                  </div>
                  <Button variant="sec" onClick={() => copyToClipboard(wallets.bnb, "BSC")} icon="copy" title="Copy address">
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </Grid2>
          {!wallets?.sol && !wallets?.btc && !wallets?.trc && !wallets?.eth && !wallets?.bnb && (
            <div style={{ textAlign: "center", padding: "20px", color: "rgba(210,235,245,.68)", fontSize: 12 }}>
              No wallet addresses found
            </div>
          )}
        </ContentGrid>
      </Card>



      <Card title="Profile Media" icon="user">
        <ContentGrid>
          <CoverImage src={cover}>
            {!cover && <CoverPlaceholder>Cover image</CoverPlaceholder>}
          </CoverImage>

          <Grid2>
            <div
              style={{ position: "relative", width: 110, height: 110, borderRadius: 22 }}
              onMouseEnter={() => setHoverAvatar(true)}
              onMouseLeave={() => setHoverAvatar(false)}
              title="Avatar preview"
            >
              <div style={{ width: "100%", height: "100%", borderRadius: 22, overflow: "hidden", border: "1px solid rgba(92,233,255,.18)", background: "linear-gradient(180deg, rgba(92,233,255,.18), rgba(0,247,210,.10))", display: "grid", placeItems: "center" }}>
                {avatar ? <img src={avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Icon name="user" />}
              </div>
              <label
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: 22,
                  background: "rgba(0,0,0,.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  opacity: hoverAvatar ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  pointerEvents: hoverAvatar ? "auto" : "none",
                }}
                title="Choose image from computer"
              >
                <span style={{ color: "rgba(255,255,255,.92)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="pencil" />
                </span>
                <FileInput
                  type="file"
                  accept="image/*"
                  disabled={avatarUploading}
                  onChange={(e) => e.target.files && void setAvatarFromFile(e.target.files[0])}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            <ContentGrid>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <FileInputLabel>
                  <Button variant="sec" disabled={avatarUploading}>
                    {avatarUploading ? "Uploading…" : "Upload avatar"}
                  </Button>
                  <FileInput type="file" accept="image/*" disabled={avatarUploading} onChange={(e) => e.target.files && void setAvatarFromFile(e.target.files[0])} />
                </FileInputLabel>
                {saveProfileImage && (
                  <Button
                    variant="sec"
                    disabled={!pendingAvatarFile || avatarUploading}
                    onClick={() => void saveProfileImage()}
                    title="Save profile image to server"
                  >
                    Save
                  </Button>
                )}
                <Button
                  variant="ghost"
                  disabled={!avatar}
                  onClick={() => {
                    clearPendingAvatar?.();
                    setAvatar(userData?.image || "");
                    pushToast("Profile", "Avatar removed");
                    setAudit((s) => [{ id: uid(), ts: "2026-01-08 09:10", actor: "user", type: "Profile", detail: "Avatar removed", severity: "low" }, ...s]);
                  }}
                  title="Remove avatar"
                  icon="x"
                >
                  Remove
                </Button>
                <FileInputLabel>
                  <Button
                    type="button"
                    variant="sec"
                    disabled={coverUploading}
                    onClick={() => coverFileInputRef.current?.click()}
                  >
                    {coverUploading ? "Uploading…" : "Upload cover"}
                  </Button>
                  <FileInput
                    ref={coverFileInputRef}
                    type="file"
                    accept="image/*"
                    disabled={coverUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setCoverFromFile(file);
                      e.target.value = "";
                    }}
                  />
                </FileInputLabel>
                {saveCoverImage && (
                  <Button
                    variant="sec"
                    disabled={!pendingCoverFile || coverUploading}
                    onClick={() => void saveCoverImage()}
                    title="Save cover image to server"
                  >
                    Save cover
                  </Button>
                )}
                {pendingCoverFile && clearPendingCover && (
                  <Button variant="ghost" onClick={() => clearPendingCover()} title="Cancel cover selection" icon="x">
                    Cancel cover
                  </Button>
                )}
              </div>

              <ContentGrid>
                <div style={{ fontWeight: 900 }}>Choose a preset avatar</div>
                <AvatarGrid>
                  {AVATAR_PRESETS.map((p) => {
                    const src = `${AVATAR_IMAGES_PATH}/${p.src}`;
                    const active = avatar === src;
                    return (
                      <AvatarPresetButton
                        key={p.id}
                        onClick={() => {
                          setAvatar(src);
                          onPresetAvatarSelected?.(src);
                          pushToast("Profile", "Avatar set: " + p.label);
                          setAudit((s) => [{ id: uid(), ts: "2026-01-08 09:10", actor: "user", type: "Profile", detail: "Avatar preset: " + p.id, severity: "low" }, ...s]);
                        }}
                        active={active}
                        title={p.label}
                        aria-label={"Avatar preset " + p.label}
                      >
                        <img src={src} alt={p.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </AvatarPresetButton>
                    );
                  })}
                </AvatarGrid>
                <div style={{ fontSize: 12, color: "rgba(210,235,245,.68)" }}>Choose a preset or upload your own image.</div>
              </ContentGrid>
            </ContentGrid>
          </Grid2>
        </ContentGrid>
      </Card>


    </ContentGrid>
  );
};

export default ProfileView;
